import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, RotateCcw, Save, X } from "lucide-react";
import { HelpTip } from "@/components/HelpTip";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { NumInput } from "@/components/NumInput";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, pct } from "@/lib/api";

export const CONTRACT_TYPES = [
  { key: "clt", label: "CLT", short: "CLT" },
  { key: "horista", label: "Horista", short: "Horista" },
  { key: "diarista", label: "Diarista", short: "Diarista" },
  { key: "freelancer", label: "Freelancer", short: "Free" },
];
const GROUPS = [
  { key: "sociais", label: "Encargos sociais", help: "Contribuições pagas pela empresa ao governo/terceiros sobre a folha: INSS patronal, RAT, Sistema S, FGTS. Empresas do Simples Nacional (anexos I, II, III e V) NÃO pagam INSS patronal, RAT e Terceiros — zere essas linhas se for o seu caso." },
  { key: "salariais", label: "Encargos salariais (provisões)", help: "Direitos do funcionário que você paga depois, mas precisa provisionar todo mês: férias, 1/3 de férias, 13º, multa do FGTS na rescisão e os encargos que incidem sobre essas provisões." },
  { key: "outros", label: "Outros encargos / benefícios", help: "Linhas personalizadas: vale-transporte, plano de saúde, alimentação. Use % sobre o salário base." },
];

export const ChargesSettings = ({ onSaved }) => {
  const { user, refresh } = useAuth();
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (user?.payroll_charges) setRows(user.payroll_charges.map((c) => ({ ...c, applies: { ...c.applies }, pct: String(c.pct) }))); }, [user]);

  const setRow = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const toggle = (i, t) => setRow(i, { applies: { ...rows[i].applies, [t]: !rows[i].applies[t] } });
  const addRow = (group) => setRows([...rows, { key: `custom_${Date.now()}`, label: "", group, pct: "0", applies: { clt: true, horista: true, diarista: false, freelancer: false } }]);
  const totalFor = (t) => rows.filter((r) => r.applies[t]).reduce((a, r) => a + (Number(r.pct) || 0), 0);

  const save = async (charges) => {
    setSaving(true);
    try {
      await api.put("/auth/payroll-charges", { charges });
      await refresh();
      toast.success("Alíquotas salvas — folha recalculada");
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="charges-settings">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {CONTRACT_TYPES.map((t) => (
          <div key={t.key} className="rounded-xl border border-border bg-card p-4" data-testid={`charges-total-${t.key}`}>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">{t.label}</p>
            <p className="num text-2xl font-bold text-primary mt-1">{pct(totalFor(t.key), 2)}</p>
            <p className="text-[11px] text-muted-foreground">de encargos sobre o salário base</p>
          </div>
        ))}
      </div>

      {GROUPS.map((g) => (
        <div key={g.key} className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`charges-group-${g.key}`}>
          <div className="flex items-center justify-between px-5 py-3 border-b border-border">
            <h3 className="font-display font-semibold flex items-center gap-2">{g.label}<HelpTip title={g.label} testId={`help-charges-${g.key}`}>{g.help}</HelpTip></h3>
            <Button size="sm" variant="ghost" onClick={() => addRow(g.key)} data-testid={`charges-add-${g.key}`}><Plus className="w-4 h-4 mr-1" />Linha</Button>
          </div>
          <div className="hidden md:grid grid-cols-12 gap-2 px-5 py-2 text-[11px] uppercase tracking-wider text-muted-foreground" data-testid={`charges-header-${g.key}`}>
            <span className="col-span-5">Encargo</span><span className="col-span-2 text-right pr-5">Alíquota %</span>
            {CONTRACT_TYPES.map((t) => <span key={t.key} className="col-span-1 text-center truncate" title={t.label}>{t.short}</span>)}<span className="col-span-1" />
          </div>
          {rows.map((r, i) => r.group === g.key && (
            <div key={r.key} className="grid grid-cols-12 gap-2 items-center px-5 py-2 border-t border-border/50" data-testid={`charge-row-${r.key}`}>
              <div className="col-span-12 md:col-span-5"><Input value={r.label} onChange={(e) => setRow(i, { label: e.target.value })} placeholder="Nome do encargo" className="h-9" data-testid={`charge-label-${r.key}`} /></div>
              <div className="col-span-3 md:col-span-2 flex items-center gap-1"><NumInput step="0.01" min="0" value={r.pct} onChange={(e) => setRow(i, { pct: e.target.value })} className="h-9 num text-right px-2" data-testid={`charge-pct-${r.key}`} /><span className="text-xs text-muted-foreground">%</span></div>
              <div className="col-span-7 md:col-span-4 grid grid-cols-4 gap-1 md:gap-2">
                {CONTRACT_TYPES.map((t) => (
                  <label key={t.key} className="flex flex-col md:flex-row items-center justify-center gap-1 text-[10px] text-muted-foreground cursor-pointer min-w-0">
                    <Checkbox checked={!!r.applies[t.key]} onCheckedChange={() => toggle(i, t.key)} data-testid={`charge-apply-${r.key}-${t.key}`} /><span className="md:hidden truncate max-w-full">{t.short}</span>
                  </label>
                ))}
              </div>
              <button type="button" className="col-span-2 md:col-span-1 flex justify-end text-muted-foreground hover:text-rose-500" onClick={() => setRows(rows.filter((_, idx) => idx !== i))} data-testid={`charge-remove-${r.key}`}><X className="w-4 h-4" /></button>
            </div>
          ))}
          {!rows.some((r) => r.group === g.key) && <p className="px-5 py-4 text-sm text-muted-foreground">Nenhuma linha neste grupo.</p>}
        </div>
      ))}

      <div className="flex flex-col sm:flex-row gap-2 justify-end">
        <Button variant="outline" onClick={() => save(null)} disabled={saving} data-testid="charges-reset"><RotateCcw className="w-4 h-4 mr-2" />Restaurar padrão</Button>
        <Button onClick={() => save(rows.map((r) => ({ ...r, pct: Number(r.pct) || 0 })))} disabled={saving} data-testid="charges-save"><Save className="w-4 h-4 mr-2" />{saving ? "Salvando..." : "Salvar alíquotas"}</Button>
      </div>
    </div>
  );
};
